import React, { useEffect, useRef, useState } from "react";
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { getDoc, doc, where, getDocs, query, collection, setDoc, updateDoc, Timestamp, onSnapshot } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import db from "../../firebase/firebaseConfig";
import { Autocomplete, TextField, Typography, IconButton } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CancelIcon from "@mui/icons-material/Cancel";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLoading } from "../Loading";
import { rejectApplication } from "../../utils/api";

function ApplicationStatus() {
    const { setLoading } = useLoading();
    const steps = [
        { label: "Application", completedLabel: "Accepted" },
        { label: "Schedule a Visit", completedLabel: "Visit Scheduled" },
        { label: "Awaiting Visit", completedLabel: "Adoption Successful" },
    ];
    const { id } = useParams();
    const navigate = useNavigate();
    const [applicationStatus, setApplicationStatus] = useState();
    const [bookedSlots, setBookedSlots] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const emailSent = useRef(false);
    const today = new Date();
    const todayTimestamp = Timestamp.fromDate(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(),
        today.getUTCHours(), today.getUTCMinutes(), today.getUTCSeconds(), today.getUTCMilliseconds())));

    const applicationId = id;
    const handleClickOpen = () => {
        const appointmentTime = appointmentData.appointmentTime.toDate();
        const currentTime = new Date();
        const timeDifference = appointmentTime - currentTime;
        const twoHoursInMilliseconds = 2 * 60 * 60 * 1000;

        if (timeDifference > twoHoursInMilliseconds) {
            setOpenDialog(true);
        }
        else {
            alert("You cannot cancel this appointment as it is within 2 hours of the scheduled time.");
        }

    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    const handleCancel = async () => {
        setOpenDialog(false);

        const updateCancelRef = doc(db, "Appointments", applicationId);
        await updateDoc(updateCancelRef, { status: "Cancel" });

        setAppointmentData((prev) => ({ ...prev, status: "Cancel" }));


    };

    const getDefaultDate = () => {
        const today = dayjs();
        const dayOfWeek = today.day(); // 0 = Sunday, 6 = Saturday

        if (dayOfWeek === 6) return today.add(2, "day"); // Saturday → Monday
        if (dayOfWeek === 0) return today.add(1, "day"); // Sunday → Monday

        return today;
    };

    const [appointmentData, setAppointmentData] = useState({
        petId: "",
        adopterId: "",
        shelterId: "",
        date: getDefaultDate(),
        time: "",
        status: ""
    });

    const [shelterData, setShelterData] = useState({
        address: "",
        phoneNumber: "",
        shelterName: "",
    });

    useEffect(() => {

        const fetchAppointment = async () => {

            const appointmentRef = doc(db, "Appointments", applicationId);
            const appointmentUnsubscribe = onSnapshot(appointmentRef, (appointmentSnap) => {
                if (appointmentSnap.exists()) {
                    const appointmentDetail = appointmentSnap.data();
                    const updatedDetail = { ...appointmentDetail, date: dayjs(appointmentDetail.date, "MM/DD/YYYY") };
                    setAppointmentData(updatedDetail);
                }

                setLoading(false);
            }, (error) => {
                console.log(error);
                setLoading(false);
            });
            return () => appointmentUnsubscribe();

        };

        fetchAppointment();

    }, [applicationId]);

    const isWeekend = (date) => {
        const day = date.day();
        return day === 0 || day === 6;
    };

    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 9; hour < 18; hour++) {
            slots.push(`${hour}:00`, `${hour}:30`);
        }
        return slots.filter(slot => !bookedSlots.includes(slot));
    };

    dayjs.extend(utc);
    dayjs.extend(timezone);
    useEffect(() => {
        const fetchBookedSlots = async () => {
            if (appointmentData && (appointmentData.status === "" || appointmentData.status === "Cancel")) {
                setAppointmentData({ ...appointmentData, time: "" });
                const appointmentRef = query(collection(db, "Appointments"), where("status", "==", "Pending"),
                    where("shelterId", "==", appointmentData.shelterId), where("date", "==", dayjs(appointmentData.date).format("MM/DD/YYYY")));

                const unsubscribe = onSnapshot(appointmentRef, (appointmentSnap) => {
                    let bookedTimes;
                    if (!appointmentSnap.empty) {
                        bookedTimes = appointmentSnap.docs.map(doc => doc.data().time);
                        setBookedSlots(bookedTimes);

                    }
                    else {
                        setBookedSlots([]);
                    }
                }, (error) => {
                    console.log(error);
                });
                return () => unsubscribe();

            }
        };
        fetchBookedSlots();

    }, [appointmentData.date, appointmentData.status]);

    useEffect(() => {
        const allSlots = generateTimeSlots();
        let filteredSlots = allSlots.filter(slot => !bookedSlots.includes(slot));

        const now = dayjs().tz("Asia/Kuala_Lumpur");
        if (dayjs(appointmentData.date).isSame(now, "day")) {
            filteredSlots = filteredSlots.filter(time =>
                dayjs(time, "HH:mm").isAfter(dayjs(now.format("HH:mm"), "HH:mm"))
            );
        }
        setAvailableTimes(filteredSlots);
    }, [bookedSlots]);


    useEffect(() => {
        const fetchApplication = async () => {
            if (!id) {
                navigate("/");
                return;
            }
            setLoading(true);
            const applicationRef = doc(db, "Applications", applicationId);

            const unsubscribeApplication = onSnapshot(applicationRef, (applicationSnap) => {

                (async () => {
                    if (applicationSnap.exists()) {
                        const applicationData = applicationSnap.data();
                        setApplicationStatus(applicationData.status);
                        setAppointmentData(prevData => ({
                            ...prevData,
                            petId: applicationData.petId,
                            adopterId: applicationData.adopterId,
                            applicationReason: applicationData.reason,
                            dueDate: applicationData.dueDate,

                        }));

                        if (applicationData.status === "Approved" && applicationData.dueDate) {

                            // Convert dueDate to a Date object for comparison
                            const dueDate = applicationData.dueDate.toDate();
                            const currentDate = new Date();

                            if (dueDate < currentDate) {
                                const appointmentRef = doc(db, "Appointments", applicationId);
                                const appointmentSnap = await getDoc(appointmentRef);

                                if ((!appointmentSnap.exists() || appointmentSnap.data().status === "Cancel") &&
                                    applicationData.status !== "Rejected") {
                                    if (!emailSent.current) {
                                        emailSent.current = true;
                                        await updateDoc(applicationRef, {
                                            status: "Rejected",
                                            dueDate: null,
                                            reason: "You have missed the deadline to book your appointment.",
                                        });

                                        try {
                                            const petRef = doc(db, 'Pets', applicationData.petId);
                                            const petSnap = await getDoc(petRef);
                                            if (petSnap.exists()) {
                                                await rejectApplication(applicationData.adopterId, petSnap.data()?.petName, "You have missed the deadline to book your appointment.");


                                            } else {
                                                console.error("pet document does not exist");
                                            }

                                        } catch (error) {
                                            console.error("Error sending email:", error);
                                        }
                                    }
                                    if (unsubscribeApplication) {
                                        unsubscribeApplication(); // Detach the listener on unmount
                                    }
                                }
                            }
                        }
                    }
                    else {
                        navigate("/");
                    }
                    setLoading(false);
                })();
            }, (error) => {
                console.log(error);
            });
            return () => {
                if (unsubscribeApplication) {
                    unsubscribeApplication(); // Detach the listener on unmount
                }
            };

        };
        fetchApplication();

    }, [applicationId, setApplicationStatus, setAppointmentData]);

    useEffect(() => {
        const fetchShelterID = async () => {
            if (!appointmentData.petId) return;

            const petRef = doc(db, "Pets", appointmentData.petId);
            const petSnap = await getDoc(petRef);
            if (petSnap.exists()) {
                const petData = petSnap.data();
                setAppointmentData({ ...appointmentData, shelterId: petData.shelterId })
            }
        };
        fetchShelterID();

    }, [appointmentData.petId])

    const renderContent = () => {
        switch (applicationStatus) {
            case "Pending":
                return <p>Your application is being reviewed. Please wait for updates.</p>;
            case "Approved":
                return <div><p>Your application has been accepted! <br></br></p>
                    <h5 style={{ fontSize: "13px" }} >Reminder: <br />Please schedule a visit within <strong>two weeks</strong> of approval.

                        Otherwise, the application will be automatically rejected.<br />
                        Appointments cannot be canceled within <strong>2 hours </strong>of the scheduled time.</h5></div>;
            default:
                return <p>Loading application status...</p>;
        }
    };

    const bookAvailable = () => {
        if (dayjs(appointmentData.date).isBefore(dayjs(), 'day') || isWeekend(appointmentData.date)
            || !dayjs(appointmentData.date).isBefore(dayjs(appointmentData.dueDate))) {
            console.log(dayjs(appointmentData.date).isBefore(dayjs(appointmentData.dueDate)));

            return true;
        }
        else {
            return false
        }
    }

    const handleBookAppointment = async () => {
        const formattedDate = dayjs(appointmentData.date).format("MM/DD/YYYY");
        const { applicationReason, ...withoutReason } = appointmentData;
        const updatedData = { ...withoutReason, date: formattedDate, status: "Pending" };

        const appointmentRef = query(collection(db, "Appointments"), where("status", "==", "Pending"),
            where("shelterId", "==", appointmentData.shelterId), where("date", "==", dayjs(appointmentData.date).format("MM/DD/YYYY")));
        const appointmentSnap = await getDocs(appointmentRef);
        const bookedTime = appointmentSnap.docs.map(doc => doc.data().time);

        if (formattedDate && appointmentData.time !== "" && appointmentData.time && !bookedTime.includes(appointmentData.time) && availableTimes.includes(appointmentData.time)) {

            const appointmentRef = doc(db, "Appointments", applicationId);

            const [month, day, year] = formattedDate.split('/').map(Number);
            const [hours, minutes] = appointmentData.time.split(':').map(Number);
            const appointmentDate = new Date(year, month - 1, day, hours, minutes);
            const timestamp = Timestamp.fromDate(appointmentDate);
            console.log(timestamp);

            await setDoc(appointmentRef, { ...updatedData, appointmentTime: timestamp });

            setAppointmentData({ ...appointmentData, status: "Pending", appointmentTime: timestamp });

            alert("Appointment Booked Successful");
        }
        else {
            setBookedSlots(bookedTime);
            alert("Selected Date or Time is unavailable");
        }

    };

    useEffect(() => {
        if (!appointmentData.shelterId)
            return

        const fetchShelterData = async () => {

            const shelterRef = doc(db, "Shelters", appointmentData.shelterId);
            const shelterSnap = await getDoc(shelterRef);
            if (shelterSnap.exists()) {
                const { address, phoneNumber, shelterName } = shelterSnap.data();
                setShelterData({ address, phoneNumber, shelterName });
            }

        }
        fetchShelterData();
    }, [appointmentData.shelterId]);


    const status = () => {
        if (applicationStatus === "Pending")
            return 0;
        else if (applicationStatus === "Approved" && appointmentData.status === "Pending")
            return 2;
        else if (applicationStatus === "Approved" && (!appointmentData.status || appointmentData.status === "Cancel"))
            return 1;
        else if (applicationStatus === "Approved" && appointmentData.status === "Approved")
            return 3;

    }

    return (
        <div className="statusContainer">
            {applicationStatus !== "Cancel" && ((applicationStatus === "Rejected" || appointmentData.status === "Rejected") ? (

                (appointmentData.status === "Rejected" ?
                    <Stepper activeStep={2} alternativeLabel>
                        <Step completed>
                            <StepLabel>Application Submitted</StepLabel>
                        </Step>
                        <Step completed>
                            <StepLabel>Visit Scheduled</StepLabel>
                        </Step>
                        <Step completed={false}>
                            <StepLabel>Adoption Rejected</StepLabel>
                        </Step>
                    </Stepper>
                    :
                    <Stepper activeStep={1} alternativeLabel>
                        <Step completed>
                            <StepLabel>Application Submitted</StepLabel>
                        </Step>
                        <Step completed={false} >
                            <StepLabel>Application Rejected</StepLabel>
                        </Step>
                    </Stepper>

                )

            ) : (
                <Stepper activeStep={status()} alternativeLabel>
                    {steps.map((step, index) => (
                        <Step key={index} completed={index < status()}>
                            <StepLabel>
                                {index < status() ? step.completedLabel : step.label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            ))}
            {applicationStatus === "Pending" &&
                <div className="pendingContainer">
                    {renderContent()}
                    <h2>Adoption Process</h2>
                    <div className="processContainer">
                        <div className="processImg"><img src="/apply.png" alt="apply adoption" /><span>Apply and Schedule Visit <br></br>within 2 weeks</span></div>
                        <div className="processImg"><img src="/meet.png" alt="meet with shelter" /><span>Meet Shelter and wait for<br></br> Final Decision</span></div>
                        <div className="processImg"><img src="/take.png" alt="take pet home" /><span>Take Pet Home</span></div>
                    </div>
                </div>
            }
            {
                applicationStatus === "Approved" && (appointmentData.status === "" || appointmentData.status === "Cancel") &&
                <div className="bookAppointmentContainer">
                    {renderContent()}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={['DatePicker', 'TimePicker']} >

                            <div className="dateTimePicker" >
                                <DatePicker className="datePicker" value={appointmentData.date} onChange={(newValue) => setAppointmentData({ ...appointmentData, date: newValue })}
                                    label="Select Date"
                                    defaultValue={getDefaultDate()}
                                    disablePast
                                    shouldDisableDate={isWeekend}
                                    views={['year', 'month', 'day']}
                                    maxDate={appointmentData.dueDate ? dayjs(appointmentData.dueDate.toDate()) : null} />

                                <Autocomplete
                                    disablePortal
                                    options={availableTimes}
                                    value={appointmentData.time}
                                    className="timePicker"
                                    label="Select Time"
                                    onChange={(event, newValue) => setAppointmentData({ ...appointmentData, time: newValue })}
                                    renderInput={(params) => <TextField {...params} label="Select Time" />}
                                />

                            </div>
                        </DemoContainer>
                    </LocalizationProvider>
                    <button className="bookBtn" disabled={!appointmentData.date || appointmentData.time === "" || appointmentData.time === null
                        || dayjs(appointmentData.date).isBefore(dayjs(), 'day') || isWeekend(appointmentData.date) ||
                        !dayjs(appointmentData.date).isBefore(dayjs(appointmentData.dueDate.toDate()))} onClick={handleBookAppointment}>Book</button>

                </div>
            }
            {
                applicationStatus === "Approved" && appointmentData.status === "Pending" &&
                <div className="AppointmentDetailsContainer">
                    <h2>Appointment Details</h2>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderRadius: "10px",
                            backgroundColor: "#fff",
                            maxWidth: "100%",

                        }}
                    >
                        {/* Left Section */}
                        <Box textAlign="left">
                            <Typography fontWeight="bold">{shelterData.shelterName}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                {shelterData.phoneNumber}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                {shelterData.address} </Typography>
                        </Box>

                        {/* Right Section */}
                        <Box textAlign="right">
                            <Typography fontWeight="bold">{dayjs(appointmentData.date).format("MM/DD/YYYY")}</Typography>
                            <Typography variant="body2">{appointmentData.time}</Typography>
                        </Box>

                        {/* Icons */}
                        <Box display="flex" gap={1}>
                            <IconButton color="primary">
                                <a href={`https://www.google.com/maps?q=${shelterData.address}`}
                                    target="_blank" rel="noopener noreferrer"><LocationOnIcon /></a>
                            </IconButton>
                            <IconButton color="error" onClick={handleClickOpen}
                                disabled={appointmentData.appointmentTime < todayTimestamp}>
                                <CancelIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <Dialog style={{ zIndex: "99999" }}
                        open={openDialog}
                        onClose={handleClose}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description">
                        <DialogTitle id="alert-dialog-title">
                            {"Do you want to cancel this visit appointment?"}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                <Box display="flex" gap={2}>
                                    <Typography fontWeight="bold">{dayjs(appointmentData.date).format("MM/DD/YYYY")}</Typography>
                                    <Typography fontWeight="bold">{appointmentData.time}</Typography>
                                </Box>

                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>No</Button>
                            <Button onClick={handleCancel} autoFocus>
                                Yes </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            }

            {
                applicationStatus === "Approved" && appointmentData.status === "Approved" &&
                <div className="adoptionSuccessContainer">
                    <h2>Congratulations! You’re Now a Pet Parent!</h2>
                    <div className="chatbotDescription">
                        <div className="chatbotText">
                            <h3>Need Help? Our Chatbot is Here for You!</h3>
                            <p>How Can the Chatbot Help?</p>
                            <p>🐾 Pet Care Tips – Ask about feeding, grooming, and training.</p>
                            <p>🐾 Behavioral Advice – Get tips on helping your pet adjust to their new home.</p>
                            <button className="statusToChatbotBtn" onClick={() => navigate('/chatbot')}>Get Started</button>
                        </div>
                        <img src="/adoptSuccess.png" alt="Success to adopt pet"></img>
                    </div>

                </div>
            }

            {
                (applicationStatus === "Rejected" || appointmentData.status === "Rejected") &&

                <div>
                    <div className="adoptionSuccessContainer">
                        <h2 style={{ margin: "0px" }}>We sincerely appreciate your interest in adoption.</h2>
                        <h2 style={{ margin: "0px" }}>We regret to inform you that it has been<strong> REJECTED</strong> at this time.</h2>
                        <p className="rejectedReason
                            ">Reason for Rejection: {applicationStatus === "Rejected" ? appointmentData.applicationReason : appointmentData.reason}</p>
                    </div>
                    <div className="chatbotDescription">
                        <div className="chatbotText">
                            <h3>What's Next?</h3>
                            <a style={{ textDecoration: "none" }} href="/adopt"><p>🐾 Browse other adorable pets looking for a home.</p></a>
                            <a style={{ textDecoration: "none" }} href="mailto:pawtner1234@gmail.com"><p>📞 Contact us if you have any questions or would like guidance on future applications.</p></a>
                        </div>
                        <img src="/rejected.png" alt="What's Next"></img>
                    </div>
                </div>


            }
            {
                applicationStatus === "Cancel" &&
                <div>
                    <img className="cancelBtn" alt="cancelled" src="/cancel.png"></img>
                    <h3 style={{ color: "#26b8fb", margin: "5%" }}>Your adoption application has been successfully canceled.</h3>
                    <div className="chatbotDescription">
                        <div className="chatbotText">
                            <h6 style={{ textAlign: "left" }}>Thank you for your interest in pet adoption!</h6>
                            <a style={{ textDecoration: "none" }} href="/adopt"><p>🐾 Browse other adorable pets looking for a home.</p></a>
                            <a style={{ textDecoration: "none" }} href="mailto:pawtner1234@gmail.com"><p>📞 Contact us if you have any questions or would like guidance on future applications.</p></a>
                        </div>
                        <img src="/rejected.png" alt="What's Next"></img>
                    </div>
                </div>
            }
        </div>
    );
}


export default ApplicationStatus;